import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'

const STORAGE_KEY = 'templatean.cookieConsent.v1'

type Choice = 'accepted' | 'declined'

function readChoice(): Choice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'accepted' || v === 'declined' ? v : null
  } catch {
    return null
  }
}

function storeChoice(choice: Choice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice)
  } catch {
    /* private mode: the card simply reappears next visit */
  }
}

/**
 * blue2purple-style cookie card: a sharp white box pinned bottom-left with
 * Accept all / Decline all. The choice persists in localStorage; closing with
 * the X dismisses for the session only. No tracking scripts are gated yet —
 * wire analytics loading to the stored value when they arrive.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (readChoice()) return
    // Appear after the hero has had a beat, like the reference.
    const t = window.setTimeout(() => setVisible(true), 1200)
    return () => window.clearTimeout(t)
  }, [])

  if (!visible) return null

  const choose = (choice: Choice) => {
    storeChoice(choice)
    setVisible(false)
  }

  return (
    <aside
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-5 left-5 z-[60] w-[min(21rem,calc(100vw-2.5rem))] bg-white dark:bg-[#12121a] border border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-700)] shadow-[var(--landing-shadow-lg)] p-5"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => setVisible(false)}
        className="absolute top-3 right-3 p-1 text-[var(--color-landing-gray-500)] hover:text-[var(--color-landing-gray-900)] dark:hover:text-white transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      <p className="text-base font-bold text-[var(--color-landing-gray-900)] dark:text-white mb-2">
        This website uses cookies
      </p>
      <p className="text-xs leading-relaxed text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)] mb-4">
        We use cookies to personalise content and analyse our traffic. We share
        usage information with our analytics partners, who may combine it with
        other information you&apos;ve provided to them.{' '}
        <Link to="/privacy" className="text-[var(--color-landing-accent)] hover:underline">
          Privacy Policy
        </Link>
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => choose('accepted')}
          className="h-9 px-4 text-xs font-bold uppercase tracking-wide text-white bg-[var(--color-landing-accent)] hover:bg-[var(--color-landing-accent-hover)] transition-colors"
        >
          Accept all
        </button>
        <button
          type="button"
          onClick={() => choose('declined')}
          className="h-9 px-4 text-xs font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] dark:text-white border border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-600)] hover:border-[var(--color-landing-gray-900)] dark:hover:border-white transition-colors"
        >
          Decline all
        </button>
      </div>
    </aside>
  )
}

export default CookieConsent
